import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
    Container,
    Typography,
    Card,
    CardContent,
    TextField,
    Button,
    Grid,
    Box,
    AppBar,
    Toolbar,
    Alert,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import EditIcon from '@mui/icons-material/Edit';
import axios from 'axios';
import logo from '../assets/logo.png';
import GovernmentSelectors from '../components/GovernmentSelectors';


function FarmerRegistration() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const editId = searchParams.get('edit');
    const isEditMode = Boolean(editId);

    const [formData, setFormData] = useState({
        name: '',
        mobile: '',
        village: '',
        district: '',
        state: '',
        field_area: '',
        crop_name: '',
        crop_season: '',
    });
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);
    const [loading, setLoading] = useState(isEditMode);

    useEffect(() => {
        if (isEditMode) {
            fetchFarmerData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchFarmerData = async () => {
        try {
            const response = await axios.get(`/api/farmers/${editId}`);
            setFormData(response.data);
            setLoading(false);
        } catch (error) {
            setError('Failed to load farmer data');
            setLoading(false);
            console.error(error);
        }
    };

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value,
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        try {
            let response;
            if (isEditMode) {
                // Update existing farmer
                response = await axios.put(`/api/farmers/${editId}`, {
                    ...formData,
                    field_area: parseFloat(formData.field_area),
                });
            } else {
                // Create new farmer
                response = await axios.post('/api/farmers', {
                    ...formData,
                    field_area: parseFloat(formData.field_area),
                });
            }

            setSuccess(true);
            setTimeout(() => {
                if (isEditMode) {
                    navigate('/'); // Go back to dashboard after edit
                } else {
                    navigate(`/soil-data-entry/${response.data.id}`);
                }
            }, 1500);
        } catch (err) {
            const detail = err.response?.data?.detail;
            const errorMsg = typeof detail === 'string'
                ? detail
                : (typeof detail === 'object' ? JSON.stringify(detail) : `Failed to ${isEditMode ? 'update' : 'register'} farmer. Please try again.`);
            setError(errorMsg);
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', backgroundColor: '#000000' }}>
                <Typography variant="h5" sx={{ color: '#4CAF50' }}>Loading...</Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ minHeight: '100vh', backgroundColor: '#000000' }}>
            <AppBar position="static">
                <Toolbar sx={{ px: { xs: 1, sm: 2 } }}>
                    <img
                        src={logo}
                        alt="SoilSense Logo"
                        style={{
                            height: '40px',
                            marginRight: '8px',
                            filter: 'drop-shadow(0 0 10px rgba(76, 175, 80, 0.5))'
                        }}
                    />
                    <Typography
                        variant="h6"
                        component="div"
                        sx={{
                            flexGrow: 1,
                            fontWeight: 600,
                            fontSize: { xs: '1rem', sm: '1.25rem' },
                            textShadow: '0 0 10px rgba(76, 175, 80, 0.5)'
                        }}
                    >
                        <span className="text-shimmer">{isEditMode ? 'Edit Farmer' : 'Farmer Registration'}</span>
                    </Typography>
                    <Button
                        color="inherit"
                        startIcon={<ArrowBackIcon />}
                        onClick={() => navigate('/')}
                        sx={{ fontSize: { xs: '0.75rem', sm: '1rem' } }}
                    >
                        Back
                    </Button>
                </Toolbar>
            </AppBar>

            <Container maxWidth="md" sx={{ mt: 4, pb: 4 }}>
                <Card className="premium-card">
                    <CardContent sx={{ p: { xs: 2, sm: 4 } }}>
                        <Typography
                            variant="h4"
                            gutterBottom
                            className="text-shimmer"
                            sx={{ mb: 3, textAlign: 'center', fontSize: { xs: '1.5rem', sm: '2.125rem' } }}
                        >
                            {isEditMode ? 'Edit Farmer Information' : 'Register New Farmer'}
                        </Typography>

                        {error && (
                            <Alert severity="error" sx={{ mb: 2, backgroundColor: 'rgba(211, 47, 47, 0.1)' }}>
                                {error}
                            </Alert>
                        )}

                        {success && (
                            <Alert severity="success" sx={{ mb: 2, backgroundColor: 'rgba(76, 175, 80, 0.1)' }}>
                                Farmer {isEditMode ? 'updated' : 'registered'} successfully! Redirecting...
                            </Alert>
                        )}

                        <form onSubmit={handleSubmit}>
                            <Grid container spacing={3}>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        required
                                        label="Farmer Name"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleChange}
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        required
                                        label="Mobile Number"
                                        name="mobile"
                                        value={formData.mobile}
                                        onChange={handleChange}
                                        inputProps={{ pattern: '[0-9]{10}', maxLength: 10 }}
                                    />
                                </Grid>

                                <Grid item xs={12}>
                                    <GovernmentSelectors
                                        formData={formData}
                                        setFormData={setFormData}
                                    />
                                </Grid>

                                <Grid item xs={12} md={12}>
                                    <TextField
                                        fullWidth
                                        required
                                        label="Field Area (hectares)"
                                        name="field_area"
                                        type="number"
                                        value={formData.field_area}
                                        onChange={handleChange}
                                        inputProps={{ min: 0, step: 0.1 }}
                                    />
                                </Grid>

                                <Grid item xs={12}>
                                    <Button
                                        type="submit"
                                        variant="contained"
                                        size="large"
                                        fullWidth
                                        startIcon={isEditMode ? <EditIcon /> : <PersonAddIcon />}
                                        className="glow-green"
                                        sx={{
                                            mt: 2,
                                            py: 1.5,
                                            fontSize: '1.1rem',
                                            backgroundColor: '#4CAF50',
                                            '&:hover': {
                                                backgroundColor: '#45a049',
                                                boxShadow: '0 0 20px rgba(76, 175, 80, 0.5)',
                                            },
                                        }}
                                    >
                                        {isEditMode ? 'Update Farmer' : 'Register Farmer'}
                                    </Button>
                                </Grid>
                            </Grid>
                        </form>
                    </CardContent>
                </Card>
            </Container>

            {/* Footer */}
            <Box
                sx={{
                    textAlign: 'center',
                    py: 3,
                    borderTop: '1px solid rgba(76, 175, 80, 0.2)',
                    backgroundColor: 'rgba(0, 0, 0, 0.3)',
                    backdropFilter: 'blur(10px)'
                }}
            >
                <Typography
                    variant="body2"
                    sx={{
                        color: '#81C784',
                        fontWeight: 500,
                        letterSpacing: '0.5px'
                    }}
                >
                    <Typography
                        variant="body2"
                        component="a"
                        href="https://github.com/Paldeepak079/SoilSense---Smart-Fertilizer-Recommendation-System"
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                            color: '#81C784',
                            fontWeight: 500,
                            letterSpacing: '0.5px',
                            textDecoration: 'none',
                            cursor: 'pointer',
                            '&:hover': {
                                color: '#A5D6A7',
                                textDecoration: 'underline'
                            }
                        }}
                    >
                        Crafted by Team_MADTech
                    </Typography>
                </Typography>
            </Box>
        </Box>
    );
}

export default FarmerRegistration;
